import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LoadUser } from '@macs-frontend/web-macs/shared';
import { Actions, createEffect } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { ofAction } from 'macs-ngrx-actions';
import {
  ClearAsyncErrorAction,
  FormGroupState,
  SetAsyncErrorAction,
  SetErrorsAction,
  StartAsyncValidationAction,
} from 'ngrx-forms';
import { from, Observable, of } from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import * as authActions from '../actions/auth.actions';
import { Token } from '../models/token.model';
import * as fromAuth from '../reducers';
import { LoginForm } from '../reducers';
import { AuthService } from '../services/auth.service';

@Injectable()
export class AuthEffects {
  loginUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofAction(authActions.Login),
      withLatestFrom(this.store$.pipe(select(fromAuth.getLoginForm))),
      switchMap(([_action, form]) =>
        this.authService
          .login({
            username: form.value.username,
            password: form.value.password,
          })
          .pipe(
            // @ts-expect-error
            mergeMap((resp: Token) => {
              localStorage.setItem('token', resp.token);
              setTimeout(() => (window.location.href = '/'));
              return [
                new ClearAsyncErrorAction(form.id, 'unauthorized'),
                new authActions.LoginSuccess(resp.token),
                new LoadUser(),
              ];
            }),
            catchError<any, any>(error => {
              if (error.status === 401) {
                return from([
                  new SetAsyncErrorAction(form.id, 'unauthorized', form.value.username),
                  new authActions.LoginFailed(),
                ]);
              }

              return of();
            }),
            startWith(new StartAsyncValidationAction(form.id, 'unauthorized')),
          ),
      ),
    ),
  );

  sendResetEmail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofAction(authActions.SendEmail),
      withLatestFrom(this.store$.pipe(select(fromAuth.getLoginForm))),
      switchMap(([_action, form]: [authActions.SendEmail, FormGroupState<LoginForm>]) =>
        this.authService.sendEmail(form.value.username).pipe(
          mergeMap(() => {
            this.router.navigate(['/auth/login']);
            return [
              new ClearAsyncErrorAction(form.id, 'notFound'),
              new authActions.SendEmailSuccess(),
            ];
          }),
          catchError<any, any>(error => {
            if (error.status === 404) {
              return from([
                new SetErrorsAction(form.id, {}),
                new SetAsyncErrorAction(form.id, 'notFound', form.value.username),
                new authActions.SendEmailFailed(),
              ]);
            }

            return of();
          }),
          startWith(new StartAsyncValidationAction(form.id, 'notFound')),
        ),
      ),
    ),
  );

  resetPassword$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofAction(authActions.ResetPassword),
      withLatestFrom(
        this.store$.pipe(select(fromAuth.getLoginForm)),
        this.store$.pipe(select(fromAuth.getResetPasswordForm)),
      ),
      switchMap(
        ([action, loginForm, resetPasswordForm]: [
          authActions.ResetPassword,
          FormGroupState<LoginForm>,
          FormGroupState<fromAuth.ResetPasswordForm>,
        ]) =>
          this.authService
            .resetPassword({
              ...action.payload,
              password: resetPasswordForm.value.password,
            })
            .pipe(
              mergeMap(() => {
                this.router.navigate(['/auth/login']);
                return [
                  new ClearAsyncErrorAction(loginForm.id, 'invalidToken'),
                  new authActions.ResetPasswordSuccess(),
                ];
              }),
              catchError<any, any>(error => {
                if (error.error.status === 444) {
                  return from([
                    new SetErrorsAction(resetPasswordForm.id, {
                      backendError: error.error.detail,
                    }),
                    new authActions.ResetPasswordFailed(),
                  ]);
                }

                return of();
              }),
              startWith(new StartAsyncValidationAction(loginForm.id, 'invalidToken')),
            ),
      ),
    ),
  );

  validateResetLink$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofAction(authActions.ValidateResetLink),
      withLatestFrom(this.store$.pipe(select(fromAuth.getLoginForm)), this.route.params),
      switchMap(
        ([action, loginForm, _params]: [
          authActions.ValidateResetLink,
          FormGroupState<LoginForm>,
          Params,
        ]) =>
          this.authService
            .validateToken(action.payload.token, action.payload.username)
            .pipe(
              map(_ => new SetErrorsAction(loginForm.id, {})),
              catchError((_error: any) => {
                this.router.navigate(['auth/send-reset-email']);

                const errorMessage =
                  _error?.error?.status === 444
                    ? _error?.error?.detail
                    : 'Sorry, but this link has expired. You can request another one below.';

                return of(
                  new SetErrorsAction(loginForm.id, {
                    invalidLink: errorMessage,
                  }),
                );
              }),
            ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private store$: Store<fromAuth.AuthState>,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}
}
